/**
 * Pure H OS · Sprint 3 · Influencer / Creator Tracker.
 * Composes Card, SectionHeader, Button, TextInput, TabBar, ListRow, Drawer, Field, Segmented.
 */
import { useMemo, useState } from "react";
import { Users, Sparkles, MapPin, Instagram } from "lucide-react";
import { Card, SectionHeader, Button, TextInput } from "../../components/lib";
import { DocumentActions } from "../../components/DocumentActions";
import { TabBar, ListRow, Drawer, Field, Segmented } from "../../components/lib.overlay";
import { useDeepLink } from "../../lib/integration";
import { CREATORS, Creator, CreatorStatus, CreatorTier, CAMPAIGNS } from "../../mocks/marketingData";

const STATUS_LABEL: Record<CreatorStatus, string> = {
  prospect: "Prospect", pitching: "Pitching", contracted: "Contracted", live: "Live", wrap: "Wrap",
};
const STATUS_TINT: Record<CreatorStatus, { bg: string; fg: string }> = {
  prospect:   { bg: "var(--ph-neutral-tint)", fg: "var(--ph-ink-muted)"     },
  pitching:   { bg: "var(--ph-warning-tint)", fg: "var(--ph-warning)"       },
  contracted: { bg: "var(--ph-brand-tint)",   fg: "var(--ph-brand-primary)" },
  live:       { bg: "var(--ph-success-tint)", fg: "var(--ph-success)"       },
  wrap:       { bg: "var(--ph-neutral-tint)", fg: "var(--ph-ink-soft)"      },
};
const TIER_LABEL: Record<CreatorTier, string> = { nano: "Nano", micro: "Micro", macro: "Macro", vip: "VIP" };

function formatAudience(n: number) {
  if (n >= 1000000) return `${(n / 1000000).toFixed(1)}M`;
  if (n >= 1000)    return `${(n / 1000).toFixed(0)}K`;
  return String(n);
}

export default function Creators() {
  const { q: initialQ } = useDeepLink();
  const [tab, setTab]         = useState<CreatorStatus | "all">("all");
  const [tier, setTier]       = useState<CreatorTier | "all">("all");
  const [q, setQ]             = useState(initialQ);
  const [selected, setSelected] = useState<Creator | null>(null);

  const counts = useMemo(() => {
    const c: Record<string, number> = { all: CREATORS.length };
    CREATORS.forEach((x) => { c[x.status] = (c[x.status] || 0) + 1; });
    return c;
  }, []);

  const filtered = useMemo(() => {
    return CREATORS.filter((c) =>
      (tab === "all"  || c.status === tab) &&
      (tier === "all" || c.tier   === tier) &&
      (!q || c.name.toLowerCase().includes(q.toLowerCase()) || c.handle.toLowerCase().includes(q.toLowerCase())),
    );
  }, [tab, tier, q]);

  return (
    <div className="space-y-6" data-testid="mkt-creators">
      <Card testId="mkt-creators-filters">
        <div className="flex items-center gap-3">
          <div className="flex-1"><TextInput value={q} onChange={setQ} placeholder="Search by name or handle…" testId="mkt-creators-search" /></div>
          <Segmented<CreatorTier | "all">
            testId="mkt-creators-tier"
            value={tier}
            onChange={setTier}
            options={[
              { value: "all",   label: "All"   },
              { value: "nano",  label: "Nano"  },
              { value: "micro", label: "Micro" },
              { value: "macro", label: "Macro" },
              { value: "vip",   label: "VIP"   },
            ]}
          />
        </div>
        <div className="mt-4">
          <TabBar<CreatorStatus | "all">
            testId="mkt-creators-tabs"
            active={tab}
            onChange={setTab}
            tabs={[
              { key: "all",        label: "All",         count: counts.all         },
              { key: "prospect",   label: "Prospects",   count: counts.prospect    },
              { key: "pitching",   label: "Pitching",    count: counts.pitching    },
              { key: "contracted", label: "Contracted",  count: counts.contracted  },
              { key: "live",       label: "Live",        count: counts.live        },
              { key: "wrap",       label: "Wrap",        count: counts.wrap        },
            ]}
          />
        </div>
      </Card>

      <Card padded={false} testId="mkt-creators-list-card">
        <div className="p-5 flex items-start justify-between flex-wrap gap-3" style={{ borderBottom: "1px solid var(--ph-border-subtle)" }}>
          <SectionHeader
            eyebrow="Roster"
            title={`${filtered.length} creator${filtered.length === 1 ? "" : "s"}`}
            action={<Users size={14} style={{ color: "var(--ph-ink-muted)" }} />}
          />
          <DocumentActions docKey="creator-report" testId="creators-doc-actions" />
        </div>
        <ul className="divide-y" style={{ borderColor: "var(--ph-border-subtle)" }} data-testid="mkt-creators-list">
          {filtered.length === 0 && (
            <li className="p-8 text-center text-sm" style={{ color: "var(--ph-ink-muted)" }}>No creators match.</li>
          )}
          {filtered.map((c) => {
            const tint = STATUS_TINT[c.status];
            return (
              <li key={c.id} data-testid={`mkt-creator-${c.id}`} className="p-4 flex items-center gap-4 cursor-pointer hover:bg-slate-50"
                  onClick={() => setSelected(c)}>
                <div
                  className="w-10 h-10 rounded-full flex items-center justify-center text-sm font-bold"
                  style={{ background: "var(--ph-brand-primary)", color: "var(--ph-ink-strong)" }}
                >
                  {c.name.charAt(0)}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-baseline gap-2 flex-wrap">
                    <div className="text-sm font-semibold" style={{ color: "var(--ph-ink-strong)" }}>{c.name}</div>
                    <div className="text-[11px]" style={{ color: "var(--ph-ink-muted)" }}>{c.handle}</div>
                    <span className="px-1.5 py-0.5 text-[9px] font-semibold uppercase" style={{ letterSpacing: "0.14em", background: "var(--ph-neutral-tint)", color: "var(--ph-ink-muted)", borderRadius: "var(--ph-radius-sm)" }}>
                      {TIER_LABEL[c.tier]}
                    </span>
                    <span className="px-1.5 py-0.5 text-[9px] font-semibold uppercase" style={{ letterSpacing: "0.14em", background: tint.bg, color: tint.fg, borderRadius: "var(--ph-radius-sm)" }}>
                      {STATUS_LABEL[c.status]}
                    </span>
                  </div>
                  <div className="text-[11px] mt-0.5 inline-flex items-center gap-3" style={{ color: "var(--ph-ink-muted)" }}>
                    <span><MapPin size={10} className="inline mr-0.5" />{c.city}</span>
                    <span className="tabular-nums">{formatAudience(c.audience)} followers</span>
                    <span className="tabular-nums">ER {(c.engagement * 100).toFixed(1)}%</span>
                  </div>
                </div>
                <div className="text-right hidden md:block">
                  <div className="inline-flex items-center gap-1 text-xs tabular-nums" style={{ color: "var(--ph-ai)" }}>
                    <Sparkles size={11} /> {c.aiFit}
                  </div>
                  <div className="text-[10px] uppercase tracking-[0.14em]" style={{ color: "var(--ph-ink-muted)" }}>AI fit</div>
                </div>
              </li>
            );
          })}
        </ul>
      </Card>

      <Drawer open={Boolean(selected)} onClose={() => setSelected(null)} title="Creator profile" testId="mkt-creator-drawer" width={460}>
        {selected && (
          <div>
            <div className="flex items-center gap-3 mb-4">
              <div className="w-12 h-12 rounded-full flex items-center justify-center text-lg font-bold" style={{ background: "var(--ph-brand-primary)", color: "var(--ph-ink-strong)" }}>
                {selected.name.charAt(0)}
              </div>
              <div>
                <div className="text-lg font-semibold" style={{ fontFamily: "var(--ph-display-font)", color: "var(--ph-ink-strong)" }}>{selected.name}</div>
                <div className="text-xs inline-flex items-center gap-1" style={{ color: "var(--ph-ink-muted)" }}><Instagram size={11} /> {selected.handle}</div>
              </div>
            </div>
            <Field label="Tier" inline><span className="text-sm">{TIER_LABEL[selected.tier]}</span></Field>
            <Field label="City" inline><span className="text-sm">{selected.city}</span></Field>
            <Field label="Audience" inline><span className="text-sm tabular-nums">{formatAudience(selected.audience)}</span></Field>
            <Field label="Engagement rate" inline><span className="text-sm tabular-nums">{(selected.engagement * 100).toFixed(1)}%</span></Field>
            <Field label="Contracted cost" inline><span className="text-sm tabular-nums">EGP {selected.costEGP.toLocaleString()}</span></Field>
            <Field label="AI fit for ORVAN" hint="Blend of audience overlap, brand tone match, and past engagement." inline>
              <span className="inline-flex items-center gap-1 text-sm tabular-nums" style={{ color: "var(--ph-ai)" }}>
                <Sparkles size={12} /> {selected.aiFit}/100
              </span>
            </Field>
            <Field label="Active campaigns" inline>
              <span className="text-xs" style={{ color: "var(--ph-ink-strong)" }}>
                {selected.campaigns.length === 0
                  ? "—"
                  : selected.campaigns.map((id) => CAMPAIGNS.find((c) => c.id === id)?.name.split(" · ")[0]).join(", ")}
              </span>
            </Field>
            <div className="pt-4 flex justify-end gap-2">
              <Button variant="secondary" testId="mkt-creator-message">Send brief</Button>
              <Button testId="mkt-creator-contract">Move to Contracted</Button>
            </div>
          </div>
        )}
      </Drawer>
    </div>
  );
}
